import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Star, ArrowRight, Menu, X, Zap, Shield, Crown, Rocket, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth, AuthModal, UserMenu } from '../auth/AuthComponents';
import { apiRequest } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import ContactSupportForm from '../shared/ContactSupportForm';
import { publicPlanLabel } from '../../utils/planLabels';

/** Lead with the monthly access reward on each tier card. */
const planRewardLine = (plan) => {
  if (plan?.name === 'enterprise') {
    return 'Reward: a custom monthly document quota, plus priority help and full platform access.';
  }
  if (plan?.name === 'free') {
    return 'Reward: 5 documents per day to try analyze, revamp, and generate.';
  }
  const n = plan?.monthly_contracts || plan?.limit || 0;
  return `Reward: ${n} documents access per month (analyze, revamp & generate), plus job alerts, SOP opportunities, and more`;
};

const planCta = (plan) => {
  if (plan?.name === 'free') return 'Start Free';
  if (plan?.name === 'enterprise') return 'Contact Us';
  return `Get ${publicPlanLabel(plan)}`;
};

const PricingPage = ({ setCurrentPage }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  const [currentSlide, setCurrentSlide] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);

  // Add floating animations and auto-scroll features
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(10px, -15px) rotate(1deg); }
        66% { transform: translate(-5px, 5px) rotate(-1deg); }
      }
      @keyframes float2 {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        50% { transform: translate(-20px, -20px) rotate(2deg); }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes scrollFeatures {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .animate-float { animation: float 8s ease-in-out infinite; }
      .animate-float2 { animation: float2 10s ease-in-out infinite; }
      .animate-slideUp { animation: slideUp 0.6s ease-out; }
      .features-scroll {
        animation: scrollFeatures 20s linear infinite;
      }
      .features-scroll:hover {
        animation-play-state: paused;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiRequest('subscription/plans');
        // Map backend plans to include color, icon, cta, and popular
        const mappedPlans = (data.plans || []).map(plan => {
          let color = 'gray', icon = <Zap />, popular = false;
          if (plan.name === 'starter') {
            color = 'blue'; icon = <Star />; popular = true;
          } else if (plan.name === 'professional') {
            color = 'purple'; icon = <Shield />; popular = false;
          } else if (plan.name === 'enterprise') {
            color = 'gold'; icon = <Crown />;
          }
          const enriched = { ...plan, color, icon, popular };
          return { ...enriched, cta: planCta(enriched) };
        });
        setPlans(mappedPlans);
      } catch (err) {
        setError('Failed to load plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const navLinks = [
    { name: 'Home', action: () => setCurrentPage('home'), current: false },
    { name: 'About', action: () => setCurrentPage('about'), current: false },
    { name: 'Pricing', href: '#', current: true },
  ];

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `₦${(price / 100).toLocaleString()}`;
  };

  const getColorClasses = (color, popular = false) => {
    const colors = {
      gray: {
        border: 'border-[var(--color-border)]',
        bg: 'elevated-card',
        icon: 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-text-muted)]'
      },
      blue: {
        border: popular ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]' : 'border-[var(--color-primary)]/40',
        bg: popular ? 'elevated-card bg-gradient-to-br from-[var(--color-primary-soft)] to-[var(--color-surface)]' : 'elevated-card',
        icon: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-primary)]'
      },
      purple: {
        border: popular ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]' : 'border-[var(--color-accent)]/40',
        bg: popular ? 'elevated-card bg-gradient-to-br from-[var(--color-primary-soft)] to-[var(--color-surface)]' : 'elevated-card',
        icon: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-primary)]'
      },
      gold: {
        border: 'border-yellow-500/40',
        bg: 'elevated-card bg-yellow-500/10',
        icon: 'bg-yellow-500/20 text-yellow-600',
        button: 'bg-yellow-600 hover:bg-yellow-700 text-[var(--color-text-inverse)] font-semibold',
        accent: 'text-yellow-600'
      }
    };
    return colors[color];
  };

  const handleSelectPlan = (plan) => {
    if (plan.name === 'enterprise') {
      setShowContactModal(true);
      return;
    }
    if (isAuthenticated) {
      navigate('/subscriptions');
    } else {
      setShowAuthModal(true);
    }
  };

  // Carousel controls
  const totalSlides = plans.length;
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Auto-advance carousel disabled
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     nextSlide();
  //   }, 8000); // Change slide every 8 seconds
  //   return () => clearInterval(interval);
  // }, [currentSlide]);

  // Render logic
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center text-[var(--color-danger)]">{error}</div>
      </div>
    );
  }
  if (!plans.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center text-[var(--color-text-muted)]">No plans available at this time.</div>
      </div>
    );
  }

  // Use plans[currentSlide] for the carousel
  const plan = plans[currentSlide];

  const isEnterprise = plan.name === 'enterprise';

  return (
    <>
      <Helmet>
        <title>Pricing – DocxsAi</title>
        <meta name="description" content="DocxsAi plans: Free, Basic Plan (10 docs), Premium Plan (50 docs), and Enterprise Plan for AI document analysis, revamp, and generation." />
        <link rel="canonical" href="https://docxsai.com/pricing" />
        <meta property="og:title" content="Pricing – DocxsAi" />
        <meta property="og:url" content="https://docxsai.com/pricing" />
      </Helmet>
      <div className="h-screen flex flex-col public-page-glow relative overflow-hidden">
        {/* Floating Elements - Adjusted for mobile */}
        <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl animate-float2"></div>
        <div className="absolute bottom-16 left-6 w-24 h-24 sm:bottom-32 sm:left-32 sm:w-64 sm:h-64 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 right-6 w-28 h-28 sm:bottom-20 sm:right-40 sm:w-72 sm:h-72 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl animate-float2"></div>
        {/* Main Content - Fill available space; top padding clears fixed/absolute nav */}
        <main className="flex-1 flex flex-col justify-center relative z-10 px-3 sm:px-6 pt-24 sm:pt-28 pb-4">
          <div className="max-w-6xl mx-auto text-center w-full animate-slideUp">
            {/* Header - Enhanced Mobile */}
            <div className="mb-3 sm:mb-4">
              <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-text)] mb-2 sm:mb-3 leading-tight">
                Choose Your{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
                  Perfect Plan
                </span>
              </h1>
              <p className="text-base sm:text-base md:text-lg text-[var(--color-text-muted)] mb-3 sm:mb-4 max-w-4xl mx-auto leading-relaxed px-4 sm:px-0">
                Unlock AI-powered document analysis, revamp, and generation. Pick a plan with a clear monthly document allowance that fits how you work.
              </p>
              {/* Billing Toggle - Enhanced Mobile */}
              <div className="flex items-center justify-center mb-2 pt-2">
                <div className="bg-[var(--color-surface)] public-input-elevated rounded-2xl p-1.5 flex">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-4 py-2.5 sm:py-2 rounded-xl font-medium transition-all text-base sm:text-sm ${
                      billingCycle === 'monthly'
                        ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className={`relative px-4 py-2.5 sm:py-2 rounded-xl font-medium transition-all text-base sm:text-sm ${
                      billingCycle === 'yearly'
                        ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 text-[9px] sm:text-[10px] font-semibold text-white bg-[var(--color-success)] px-1.5 py-0.5 rounded-full whitespace-nowrap leading-none shadow-sm">
                      -20%
                    </span>
                    Yearly
                  </button>
                </div>
              </div>
            </div>
            {/* Pricing Carousel - Enhanced Mobile */}
            <div className="relative">
              {/* Carousel Container — pt keeps Most Popular badge inside overflow clip */}
              <div className="overflow-hidden pt-3">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {plans.map((plan, slideIndex) => {
                    const colors = getColorClasses(plan.color, plan.popular);
                    const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
                    const yearlyDiscount = billingCycle === 'yearly' && plan.yearly_price > 0 && plan.monthly_price > 0 && plan.name !== 'enterprise';
                    const isEnterprise = plan.name === 'enterprise';
                    return (
                      <div key={slideIndex} className="w-full flex-shrink-0 px-3 sm:px-2">
                        <div className="max-w-4xl mx-auto public-tilt-wrap">
                          <div
                            className={`relative backdrop-blur-sm border rounded-3xl p-5 sm:p-6 transition-all duration-300 hover:scale-[1.02] ${colors.border} ${colors.bg}`}
                          >
                            {plan.popular && (
                              <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 z-10">
                                <div className="bg-[var(--color-surface)] border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium tracking-wide whitespace-nowrap">
                                  Most Popular
                                </div>
                              </div>
                            )}

                            {/* Plan Header - Enhanced Mobile Layout */}
                            <div className="mb-5 sm:mb-4">
                              {/* Top row: Icon, Title, Price */}
                              <div className="flex items-center justify-between mb-4 sm:mb-3">
                                <div className="flex items-center space-x-4 sm:space-x-3">
                                  <div className={`w-12 h-12 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${colors.icon}`}>
                                    {React.cloneElement(plan.icon, { className: "w-6 h-6 sm:w-5 sm:h-5" })}
                                  </div>
                                  <h3 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{publicPlanLabel(plan)}</h3>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-baseline">
                                    <span className="text-3xl sm:text-3xl font-bold text-[var(--color-text)]">
                                      {isEnterprise ? 'Custom' : formatPrice(price)}
                                    </span>
                                    {!isEnterprise && <span className="text-base font-medium text-[var(--color-text-muted)]">/month</span>}
                                  </div>
                                  {yearlyDiscount && (
                                    <div className="text-sm text-[var(--color-success)] mt-1">
                                      Save ₦{(((plan.monthly_price || 0) * 12 - (plan.yearly_price || 0)) / 100).toLocaleString()} per year
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Reward-first description */}
                              <div className="text-left bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-xl px-4 py-3 mb-4 sm:mb-2">
                                <p className="text-xs uppercase tracking-wide text-[var(--color-primary)] font-semibold mb-1">
                                  What you unlock
                                </p>
                                <p className="text-[var(--color-text-secondary)] text-base leading-relaxed">
                                  {planRewardLine(plan)}
                                </p>
                              </div>
                            </div>

                            {/* Features - Enhanced Mobile Scrolling */}
                            <div className="mb-5 sm:mb-4">
                              <div className="overflow-hidden relative">
                                <div className="features-scroll flex gap-3 whitespace-nowrap">
                                  {/* Duplicate features for seamless loop */}
                                  {[...Object.keys(plan.features).filter(f => plan.features[f]), ...Object.keys(plan.features).filter(f => plan.features[f])].map((feature, featureIndex) => (
                                    <div key={featureIndex} className="flex items-center space-x-2 bg-[var(--color-surface-muted)] rounded-lg px-3 py-2 sm:py-1.5 flex-shrink-0">
                                      <Check className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-[var(--color-success)] flex-shrink-0" />
                                      <span className="text-[var(--color-text-secondary)] text-sm">{feature}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* CTA Button - Enhanced Mobile */}
                            <div className="text-center">
                              <button
                                onClick={() => {
                                  if (isEnterprise) {
                                    setShowContactModal(true);
                                  } else {
                                    handleSelectPlan(plan);
                                  }
                                }}
                                className={`w-auto px-8 sm:px-6 py-4 sm:py-3 rounded-xl transition-colors text-lg sm:text-base ${
                                  isEnterprise 
                                    ? "bg-yellow-600 hover:bg-yellow-700 text-[var(--color-text-inverse)] font-semibold" 
                                    : "brand-gradient-btn"
                                }`}
                              >
                                {isEnterprise ? 'Contact Us' : plan.cta}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Carousel Controls - Enhanced Mobile */}
              <button
                onClick={prevSlide}
                className="absolute left-1 top-1/2 transform -translate-y-1/2 -translate-x-4 p-3 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)] rounded-full transition-all z-10"
              >
                <ChevronLeft className="w-6 h-6 text-[var(--color-text)]" />
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 translate-x-4 p-3 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)] rounded-full transition-all z-10"
              >
                <ChevronRight className="w-6 h-6 text-[var(--color-text)]" />
              </button>

              {/* Slide Indicators - Enhanced Mobile */}
              <div className="flex justify-center space-x-3 sm:space-x-2 mt-4 sm:mt-2">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 sm:w-2.5 sm:h-2.5 rounded-full transition-all ${
                      currentSlide === index 
                        ? 'bg-[var(--color-primary)]' 
                        : 'bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]'
                    }`}
                  />
                ))}
              </div>
            </div>
            {showContactModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'var(--color-overlay)' }}>
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-2xl p-6 max-w-lg w-full relative text-left">
                  <button
                    className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    onClick={() => setShowContactModal(false)}
                  >
                    <X className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold text-[var(--color-text)] mb-4 text-left">Contact Us About Enterprise Plan</h2>
                  <ContactSupportForm
                    initialName={user?.full_name || ''}
                    initialEmail={user?.email || ''}
                    onClose={() => setShowContactModal(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Footer - Enhanced Mobile */}
        {/* Footer removed here */}

        {/* Keyboard hint - Hidden on smaller screens */}
        <div className="absolute bottom-16 sm:bottom-20 right-4 sm:right-6 text-xs text-[var(--color-text-muted)] hidden lg:block">
          Use ← → keys to navigate
        </div>
      </div>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

export default PricingPage; 